import React, { useEffect, useState } from "react";
import { useAuth } from "../services/auth/CognitoAuthContext.js";
import AuthLoginForm from "../components/AuthLoginForm";
import UserProfile from "../components/UserProfile";
import AuthLayout from "../components/AuthLayout.jsx";
import { useNavigate, useParams } from "react-router-dom";
import AuthRegisterUser from "../components/AuthRegisterUser.jsx";
import AuthUserConfirmation from "../components/AuthUserConfirmation";
import AuthForgotPassword from "../components/AuthForgotPassword.jsx";
import AuthResetPassword from "../components/AuthResetPassword.jsx";

/**
 * Example page demonstrating how to use the CognitoIdentityService with
 * the LoginForm and SignupForm components.
 */
export default function Auth() {
  let { action } = useParams();
  const [formValues, setFormValues] = useState({});
  const [message, setMessage] = useState(null);
  const { initialised, authService, session } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // registration is in progress, push them back
    if (localStorage.getItem("register")) {
      navigate("/auth/confirm");
      return;
    }

    // enforce valid auth route
    if (!["login", "logout", "register", "confirm", "reset", "forgot"].includes(action)) {
      navigate("/auth/login");
    }
  }, [navigate, action]);

  const [redirectUri] = useState(() => {
    const redirectUri = localStorage.getItem("redirectUri");
    return redirectUri || "/";
  });

  const handleChange = (key, value) => {
    setFormValues({ ...formValues, [key]: value });
  };

  const handleSuccess = ({ action, message } = {}) => {
    // clear all other form values on success
    setFormValues({ username: formValues.username || "", email: formValues.email || "" });

    // display any message returned
    setMessage(message || null);

    switch (action) {
      case "register":
        navigate("/auth/register");
        break;

      case "confirm":
        navigate("/auth/confirm");
        break;

      case "login":
        navigate("/auth/login");
        break;

      case "reset":
        navigate("/auth/reset");
        break;

      case "forgot":
        navigate("/auth/forgot");
        break;

      case "authenticated":
        localStorage.removeItem("redirectUri");
        navigate(redirectUri ? `${redirectUri}` : "/");
        break;
    }
  };

  const handleError = (action, error) => {
    console.log(`${action} error:`, error);
  };

  const handleSignOut = async () => {
    if (initialised) {
      try {
        await authService.signOut();
        navigate("/");
        // The session listener will update the session state
      } catch (error) {
        console.error("Sign out error:", error);
      }
    }
  };

  const handleAccountSwitch = (existing) => {
    setFormValues({});
    if (existing) {
      navigate("/auth/login");
    } else {
      navigate("/auth/register");
    }
  };

  // Show loading state while initializing
  if (!initialised) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  // Show user info if authenticated
  if (session && session.authenticated) {
    return (
      <AuthLayout>
        <div className="mx-auto mt-8 max-w-md">
          <UserProfile userInfo={session.userInfo} onSignOut={handleSignOut} />
        </div>
      </AuthLayout>
    );
  }

  // Show login or signup form
  return (
    <AuthLayout>
      <div className="mx-auto mt-8 max-w-md">
        {message && <div className="text-proof-green text-center">{message}</div>}
        {action === "register" && (
          <>
            <AuthRegisterUser
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={handleSuccess}
              onError={(error) => handleError("register", error)}
            />
            <div className="mt-4 text-center">
              <a role="button" onClick={() => handleAccountSwitch(true)} className="text-proof-blue hover:opacity-80 cursor-pointer">
                Already have an account? Sign in
              </a>
            </div>
          </>
        )}

        {action === "confirm" && (
          <>
            <AuthUserConfirmation
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={handleSuccess}
              onError={(error) => handleError("confirm", error)}
            />
          </>
        )}

        {action === "forgot" && (
          <>
            <AuthForgotPassword
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={handleSuccess}
              onError={(error) => handleError("forgot", error)}
            />
            <div className="mt-4 text-center">
              <a role="button" onClick={() => handleAccountSwitch(true)} className="text-proof-blue hover:opacity-80 cursor-pointer">
                Return to Sign in
              </a>
            </div>
          </>
        )}

        {action === "reset" && (
          <>
            <AuthResetPassword
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={handleSuccess}
              onError={(error) => handleError("reset", error)}
            />
            <div className="mt-4 text-center">
              <a role="button" onClick={() => handleAccountSwitch(true)} className="text-proof-blue hover:opacity-80 cursor-pointer">
                Return to Sign in
              </a>
            </div>
          </>
        )}

        {action === "login" && (
          <>
            <AuthLoginForm
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={handleSuccess}
              onError={(error) => handleError("login", error)}
            />
            <div className="mt-4 text-center">
              <a role="button" onClick={() => handleAccountSwitch(false)} className="text-proof-blue cursor-pointer hover:opacity-80 cursor-pointer">
                Don't have an account? Sign up
              </a>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
